import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import IDUploadForm from './components/IDUploadForm';
import SubmissionTracker from './components/SubmissionTracker';
import LandingPage from './components/LandingPage';
import Header from './components/Header';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import MaintenancePage from './components/MaintenancePage';
import { SUPABASE_CONFIGURED } from './lib/supabase';
import { AlertTriangle, Settings } from 'lucide-react';
import './App.css';

type View = 'landing' | 'upload' | 'track';

function App() {
  const [currentView, setCurrentView] = useState<View>('landing');
  const isDemo = !SUPABASE_CONFIGURED;
  const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

  useEffect(() => {
    // Map pathnames to views - removed admin routes for security
    const mapPathToView = (path: string): View => {
      if (path === '/' || path === '') return 'landing';
      if (path.startsWith('/upload') || path.startsWith('/earn')) return 'upload';
      if (path.startsWith('/track')) return 'track';
      // Admin routes are now completely separate - redirect any admin attempts to landing
      return 'landing';
    };

    setCurrentView(mapPathToView(window.location.pathname));

    const onPop = () => setCurrentView(mapPathToView(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const renderView = () => {
    switch (currentView) {
      case 'landing':
        return <LandingPage 
          onGetStarted={() => handleViewChange('upload')} 
          onTrackSubmission={() => handleViewChange('track')}
        />;
      case 'upload':
        return <IDUploadForm onNavigate={(view, submissionId) => {
          if (view === 'track' && submissionId) {
            // Store the submission ID in sessionStorage for the tracking page
            sessionStorage.setItem('trackingSubmissionId', submissionId);
          }
          handleViewChange(view);
        }} />;
      case 'track':
        return <SubmissionTracker onBack={() => handleViewChange('landing')} />;
      default:
        return <LandingPage 
          onGetStarted={() => setCurrentView('upload')} 
          onTrackSubmission={() => setCurrentView('track')}
        />;
    }
  };

  const handleViewChange = (view: string) => {
    setCurrentView(view as View);
    
    // Update URL - removed admin routes
    try {
      const path = view === 'landing' ? '/' : 
                   view === 'upload' ? '/earn' :
                   view === 'track' ? '/track' :
                   `/${view}`;
      window.history.pushState({}, '', path);
    } catch (e) {
      // ignore
    }

    // Use setTimeout to ensure DOM is updated before scrolling
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 0);
  };

  // All views now show header/footer since admin is separate
  const shouldShowHeaderFooter = true;

  // Show maintenance page if enabled
  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <Toaster 
          position="top-right" 
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#4ade80',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
        
        {/* Demo Mode Banner */}
        {isDemo && (
          <div className="bg-orange-500 text-white px-4 py-3 text-center relative z-50">
            <div className="flex items-center justify-center space-x-2">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Demo Mode Active</span>
              <span className="hidden sm:inline">- Connect Supabase for full functionality</span>
            </div>
          </div>
        )}

        {/* Configuration Warning */}
        {isDemo && currentView === 'upload' && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mx-4 mt-4 relative z-40">
            <div className="flex">
              <div className="flex-shrink-0">
                <Settings className="h-5 w-5 text-blue-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>Setup Required:</strong> To use full functionality, configure your Supabase credentials in the .env file.
                  Check the SETUP.md for detailed instructions.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Header Navigation */}
        {shouldShowHeaderFooter && (
          <Header 
            currentView={currentView} 
            onViewChange={handleViewChange}
            isDemo={isDemo}
          />
        )}

        {/* Main Content */}
        <main className={shouldShowHeaderFooter ? '' : 'min-h-screen'}>
          {renderView()}
        </main>

        {/* Footer */}
        {shouldShowHeaderFooter && (
          <Footer onViewChange={handleViewChange} />
        )}
      </div>
    </ErrorBoundary>
  );
}

export default App;
